import axios, { AxiosInstance } from 'axios';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { config } from '../config/environment';
import { ApiResponse } from '../types/partner';

/**
 * Device inventory DTO - lightweight device info for partner portal.
 * Matches the telemetry function's DeviceInventoryDto.
 */
export interface DeviceInventory {
  id: string;
  customerId: string;
  name: string;
  model?: string;
  manufacturer?: string;
  serialNumber?: string;
  platform?: string;
  osVersion?: string;
  hostName?: string;
  cpuBrand?: string;
  cpuCores?: number;
  architecture?: string;
  totalMemoryGB?: number;
  agentVersion?: string;
  ownerUserId?: string;
  currentUserName?: string;
  currentUserEmail?: string;
  firstSeenUtc?: string;
  lastSeenUtc?: string;
  ageInDays?: number;
  source: 'aitryx' | 'intune';
}

/**
 * Device telemetry snapshot DTO - full device data with metrics.
 */
export interface DeviceTelemetrySnapshot {
  id: string;
  deviceId: string;
  name: string;
  deviceName: string;
  userId?: string;
  customerId: string;
  firstSeen?: string;
  lastSeen?: string;
  blobPath?: string;
  status: string;
  isOnline: boolean;
  model?: string;
  agentVersion?: string;
  cpuUsagePercent?: number;
  memoryUsedPercent?: number;
  diskFreePercent?: number;
  manufacturer?: string;
  serialNumber?: string;
  platform?: string;
  osVersion?: string;
  hostName?: string;
  cpuBrand?: string;
  cpuCores?: number;
  architecture?: string;
  biosVersion?: string;
  totalMemoryGB?: number;
  usedMemoryGB?: number;
  totalDiskGB?: number;
  usedDiskGB?: number;
  uptimeSeconds?: number;
  currentUserName?: string;
  currentUserEmail?: string;
}

/**
 * Device aggregate stats for fleet overview.
 */
export interface DeviceAggregateStats {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  warningDevices: number;
  devicesByPlatform: {
    windows: number;
    macos: number;
    linux: number;
  };
  averageMemoryUsage: number;
  averageCpuUsage: number;
  averageUptimeHours: number;
  totalStorageGB: number;
  totalUsedStorageGB: number;
}

/**
 * Device telemetry response wrapper.
 */
export interface TelemetryResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Telemetry API Service - calls func-aitryx-telemetry directly.
 * Uses AitryxCore JWT tokens for authentication.
 */
class TelemetryService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.api.telemetryUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for authentication
    this.client.interceptors.request.use(
      async (axiosConfig) => {
        const token = await ensureFreshAitryxCoreToken();
        axiosConfig.headers.Authorization = `Bearer ${token}`;
        return axiosConfig;
      },
      (error) => Promise.reject(error)
    );

    // Optional API logging
    if (config.features.enableApiLogging) {
      this.client.interceptors.request.use(
        (axiosConfig) => {
          console.log(`📊 Telemetry API Request: ${axiosConfig.method?.toUpperCase()} ${axiosConfig.url}`);
          return axiosConfig;
        }
      );

      this.client.interceptors.response.use(
        (response) => {
          console.log(`📊 Telemetry API Response: ${response.status} ${response.config.url}`);
          return response;
        },
        (error) => {
          console.error(`📊 Telemetry API Error: ${error.response?.status} ${error.config?.url}`);
          return Promise.reject(error);
        }
      );
    }
  }

  /**
   * Get device inventory for a customer (lightweight, no real-time metrics).
   * Optimized for partner portal device lists.
   */
  async getCustomerDeviceInventory(customerId: string): Promise<ApiResponse<DeviceInventory[]>> {
    try {
      const response = await this.client.get<TelemetryResponse<DeviceInventory[]>>(
        `/telemetry/tenant/${customerId}/inventory`
      );
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to get device inventory',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get device inventory',
      };
    }
  }

  /**
   * Get full device list with telemetry metrics for a customer.
   */
  async getCustomerDevices(customerId: string): Promise<ApiResponse<DeviceTelemetrySnapshot[]>> {
    try {
      const response = await this.client.get<TelemetryResponse<DeviceTelemetrySnapshot[]>>(
        `/telemetry/tenant/${customerId}/devices`
      );
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to get devices',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get devices',
      };
    }
  }

  /**
   * Get aggregate statistics for a customer's fleet.
   */
  async getCustomerDeviceOverview(customerId: string): Promise<ApiResponse<DeviceAggregateStats>> {
    try {
      const response = await this.client.get<TelemetryResponse<DeviceAggregateStats>>(
        `/telemetry/tenant/${customerId}/overview`
      );
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to get device overview',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get device overview',
      };
    }
  }

  /**
   * Get latest telemetry for a specific device.
   */
  async getDeviceLatest(deviceId: string, customerId: string): Promise<ApiResponse<DeviceTelemetrySnapshot>> {
    try {
      const response = await this.client.get<TelemetryResponse<DeviceTelemetrySnapshot>>(
        `/telemetry/device/${deviceId}/latest`,
        { params: { tenantId: customerId } }
      );
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to get device details',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get device details',
      };
    }
  }
}

// Export singleton instance
export const telemetryService = new TelemetryService();
export default telemetryService;
